"use client";

import React, { useState, useCallback } from 'react';
import styles from './_gc-step-starting-labs.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputCheckbox } from '@/components/form';
import { PEDIATRIC_SUPRAPHYSIOLOGIC_THRESHOLD } from '@/data/gc-calculator-data';

/**
 * @description GC OPTIMIZE™ Calculator — Starting Labs
 * Shown for pediatric patients on the "Starting a patient" path.
 * Introduces A4 and GC dose context, then asks the HCP to classify
 * the patient's current lab status (A4 > goal vs A4 ≤ goal + supraphysiologic).
 *
 * Inner steps:
 *   0 — Radio selection (must pick one to advance)
 *   1 — Shows recommendation text, then Save & continue dispatches
 *
 * Saved fields (via dispatch):
 *   a4Assessment — 'above-goal' | 'at-goal-supraphysiologic'
 *
 * @param {Object}   state    — wizard state from useReducer
 * @param {Function} dispatch — wizard dispatch
 * @param {Function} onBack   — navigate to previous step
 * @param {Function} onNext   — navigate to next step
 */
function GcStepStartingLabs({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  const [a4Assessment, setA4Assessment] = useState(state.formValues.a4Assessment ?? '');
  const [innerStep, setInnerStep] = useState(0);

  // Reset inner step when selection changes so the path recalculates cleanly
  const handleSelectionChange = (value) => {
    setA4Assessment(value);
    setInnerStep(0);
  };

  const isFinalInnerStep = innerStep === 1;

  const handleNextOrSave = useCallback(() => {
    if (innerStep < 1) {
      setInnerStep(1);
    } else {
      const a4Changed = a4Assessment !== state.formValues.a4Assessment;
      dispatch({ type: 'SAVE_STARTING_LABS', a4Assessment });
      onNext({ formValues: { ...state.formValues, a4Assessment }, ...(a4Changed ? { forceReadvance: true } : {}) });
    }
  }, [innerStep, dispatch, a4Assessment, onNext, state.formValues]);

  return (
    <GcStepShell
      title="Starting Labs"
      showBack={false}
      onBack={onBack}
      onNext={handleNextOrSave}
      isNextDisabled={!a4Assessment}
      isReview={isReview}
      nextLabel={isFinalInnerStep ? 'Save & continue' : 'Next'}
      nextVariant={isFinalInnerStep ? ['solid', 'solid--eggplant'] : ['solid', 'solid--orange']}
      nextIconType={isFinalInnerStep ? undefined : 'arrow-right'}
      stepNumber={stepNumber}
      innerStep={innerStep}
    >
      <div className={styles.starting_labs}>
        <p className={styles.starting_labs_intro}>
          Knowing your patient&apos;s goal A4 and GC values is key to adjusting GC doses.
        </p>

        <p className={styles.starting_labs_body}>
          For growing patients, the A4 treatment goal should generally be <span
            className="text-nowrap">1&ndash;1.5&times; ULN</span> for sex and either age or pubertal stage for morning pre-GC
          measurements, or &le;ULN for post-GC measurements.
        </p>

        <p className={styles.starting_labs_body}>
          For GC doses, 8 to 11 mg/m&sup2;/day HCe may be sufficient for cortisol
          replacement in most pediatric patients; however, some patients may require
          higher doses to prevent adrenal insufficiency. Symptoms of adrenal
          insufficiency overlap with those for GC withdrawal (ie, when reducing from
          a higher to a lower supraphysiologic dose) and may include headache,
          fatigue, abdominal pain, and/or nausea.
        </p>

        <fieldset className={styles.field_group}>
          <legend className={styles.field_label}>What are your patient&apos;s current labs?</legend>

          <FormInputCheckbox
            name="a4-above-goal"
            typeRadio
            radioGroupName="a4-assessment"
            defaultChecked={a4Assessment === 'above-goal'}
            onChange={() => handleSelectionChange('above-goal')}
          >
            Patient&apos;s A4 level is &gt; goal
          </FormInputCheckbox>

          <FormInputCheckbox
            name="a4-at-goal-supraphysiologic"
            typeRadio
            radioGroupName="a4-assessment"
            defaultChecked={a4Assessment === 'at-goal-supraphysiologic'}
            onChange={() => handleSelectionChange('at-goal-supraphysiologic')}
          >
            Patient&apos;s A4 level is &le; goal and supraphysiologic GC dose*
          </FormInputCheckbox>
        </fieldset>

        {isFinalInnerStep && (
          <p className={styles.recommendation}>
            {a4Assessment === 'above-goal'
              ? 'Assess treatment progress ~1 to 3 months after initiating CRENESSITY.'
              : <>Consider reducing daily GC dose by 10% to 20% &ge;1 month after initiating CRENESSITY.</>}
          </p>
        )}

        <p className={styles.footnote}>
          <span className="hang">*</span>A supraphysiologic GC dose in the CAHtalyst<sup style={{paddingLeft: '2px', fontStyle: 'normal'}}>&reg;</sup> Pediatric study was
          defined as &gt;{PEDIATRIC_SUPRAPHYSIOLOGIC_THRESHOLD}&nbsp;mg/m&sup2;/day.
        </p>
      </div>
    </GcStepShell>
  );
}

export default GcStepStartingLabs;
